<template>
  <form @submit.prevent="save">
    <b-message type="is-danger" v-if="errors.length > 0">
      <p v-for="error in errors">{{error}}</p>
    </b-message>
    <div class="columns is-multiline">
      <div class="column is-4">
        <b-field>
          <form-select :label="`${$t('docflow.fields.type')} *`" v-model="form.type" :disabled="isChangeRequest">
              <option v-for="type in types" :value="type.id">{{ type.name }}</option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-4">
        <b-field>
          <form-input :label="`${$t('docflow.fields.registration_number')} *`"
                      :disabled="isChangeRequest"
                      mask="9a-999"
                      v-model="form.registration_number"></form-input>
        </b-field>
      </div>
      <div class="column is-4">
          <b-field>
              <form-datepicker :label="$t('docflow.fields.registered_date')"
                               :disabled="isChangeRequest"
                               :min-date="yesterday"
                               :max-date="tomorrow"
                               v-model="form.registered_date">
              </form-datepicker>
          </b-field>
      </div>
      <div class="column is-4" v-if="!form.is_informational">
          <b-field>
              <form-datepicker :label="$t('docflow.fields.term_date')"
                               :min-date="yesterday"
                               v-model="form.term_date">
              </form-datepicker>
          </b-field>
      </div>
      <template>
        <div class="column is-4">
            <b-field>
                <form-select :label="`${$t('docflow.fields.delivery_type')} *`"
                             :disabled="isChangeRequest"
                             v-model="form.delivery_type">
                    <option v-for="type in deliveryTypes" :value="type.id">{{ type.name }}</option>
                </form-select>
            </b-field>
        </div>
        <div class="column is-4">
          <b-field>
              <form-input :label="`${$t('docflow.fields.original_document_number')} *`"
                          :disabled="isChangeRequest"
                          v-model="form.original_document_number">
              </form-input>
          </b-field>
        </div>
        <div class="column is-4">
            <b-field>
                <form-datepicker :label="$t('docflow.fields.original_document_date')"
                                 :disabled="isChangeRequest"
                                 :max-date="today"
                                 v-model="form.original_document_date">
                </form-datepicker>
            </b-field>
        </div>
        <div class="column is-4">
          <SenderSelect :initials="form.sender ? form.sender.name : null"
                        :label="`${$t('docflow.fields.sender')} *`"
                        :disabled="isChangeRequest"
                        :can-add-external="false"
                        :custom-not-found-text="$t('errors.organizationNotFound')"
                        v-model="form.sender">
          </SenderSelect>
        </div>
      </template>
      <div class="column is-12">
        <b-field>
          <form-textarea :label="`${$t('interface.description')} *`" v-model="form.description"></form-textarea>
        </b-field>
      </div>
      <div class="column is-4">
        <b-field>
          <EmployeeSelect :initials="form.resolution_author__initials"
                          :label="$t('docflow.fields.resolution_author')"
                          :disabled="isChangeRequest"
                          :scope-all="true"
                          v-model="form.resolution_author">
          </EmployeeSelect>
        </b-field>
      </div>
      <div class="column is-6">
          <span class="has-text-grey mt-5" v-html="$t('interface.selfAssign')" v-if="!form.id"></span>
      </div>
        <div class="column is-12 mt-10">
            <b-checkbox v-model="form.is_informational" :disabled="isChangeRequest">
                {{ $t('docflow.fields.is_informational') }}
            </b-checkbox>
            <b-checkbox v-model="form.is_classified" class="ml-40" :disabled="isChangeRequest">
                {{ $t('docflow.fields.is_classified') }}
            </b-checkbox>
        </div>
      <div class="column is-12 mt-5" v-if="!form.is_classified">
          <b-field class="file">
              <b-upload v-model="form.file" :disabled="isChangeRequest">
                  <a class="button is-warning" :class="{'is-static': isChangeRequest}">
                      <span>{{ $t('docflow.fields.file') }} *</span>
                  </a>
              </b-upload>
              <span class="file-name">
            <template v-if="form.file">{{ form.file.name }}</template>
            <template v-if="form.file__name && !form.file">{{ form.file__name }}</template>
            <template v-if="!form.file__name && !form.file">{{ $t('interface.noFileSelected') }}</template>
          </span>
          </b-field>
      </div>
      <div class="column is-12">
        <div class="field is-grouped mt-10">
          <div class="control">
            <button type="submit" class="button is-primary">{{ $t('interface.save') }}</button>
          </div>
          <div class="control">
            <button class="button is-danger" @click.prevent="cancel">{{ $t('interface.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import {prepareDate} from '@/utils'
  import EmployeeSelect from '@/components/EmployeeSelect'
  import SenderSelect from '@/components/SenderSelect'
  import GovernmentAgencySelect from '@/components/GovernmentAgencySelect'
  import DocumentSelect from '@/components/DocumentSelect'
  import moment from 'moment'

  const defaultDocumentData = {
    registration_number: null,
    description: '',
    category: 'incoming', //outgoing
    registered_date: new Date(),
    type: '',
    term_date: new Date(),
    file: null,
    is_informational: false,
    is_classified: false,
    resolution_author: null,
    original_document_number: null,
    original_document_date: new Date(),
    delivery_type: null,
    sender: null
  }

  export default {
    props: ['documentData', 'editMode'],
    components: {
      EmployeeSelect,
      GovernmentAgencySelect,
      DocumentSelect,
      SenderSelect
    },
    data() {
      return {
        form: this.documentData || {...defaultDocumentData},
        isEdit: this.editMode || false,
        errors: [],
        today: moment(new Date()).toDate(),
        tomorrow: moment(new Date()).add(1, 'days').toDate(),
        yesterday: moment(new Date()).add(-1, 'days').toDate(),
      }
    },
    methods: {
      validate() {
        this.errors = []
        if (this.form.name === '' || this.form.description === '' || this.form.sender === null) {
          this.errors.push(this.$t('interface.requiredFields'))
        }
        return this.errors.length === 0
      },
      save() {
        if (this.validate()) {
          this.$emit('save', {
            ...this.form,
            registered_date: this.form.registered_date ? prepareDate(this.form.registered_date) : null,
            term_date: this.form.term_date ? prepareDate(this.form.term_date) : null,
            original_document_date: this.form.original_document_date ? prepareDate(this.form.original_document_date) : null
          })
        }
      },
      cancel() {
        this.$router.go(-1)
      },
    },
    computed: {
        ...mapState('Docflow', ['types', 'deliveryTypes']),
        isChangeRequest() {
            return this.form.change_state ? this.form.change_state === 'approved' : false;
        }
    },
    watch: {
      documentData(document) {
        this.form = document
      }
    }
  }
</script>
